import { z } from 'zod';
import type { MaterialUnit } from '../materials/catalog.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Kumanya kataloğu (ADR 0099 §2–§4). Kumanya kalemi malzeme kataloğunun `Provision` türündeki
 * kalemidir; kütüphanenin alanları `materials` satırında. Listeler ortağın kütüphanesindeki
 * değerlerdir (`Provisioning_Master_Item_Library_25_Categories.xlsx`, Field Lists).
 */

export const PROVISION_TYPE = 'Provision' as const;

export const STORAGE_TYPES = ['Dry', 'Chilled', 'Frozen', 'Chemical', 'Medical'] as const;
export type StorageType = (typeof STORAGE_TYPES)[number];

/** Batch / Expiry Tracking: `Yes` zorunlu, `Optional` isteğe bağlı, `No` izlenmez. */
export const TRACKING_MODES = ['Yes', 'Optional', 'No'] as const;
export type TrackingMode = (typeof TRACKING_MODES)[number];

export const COST_CENTERS = [
  'Crew Provisions',
  'Hotel & Welfare',
  'Medical',
  'Guest Provisions',
] as const;
export type CostCenter = (typeof COST_CENTERS)[number];

export const VESSEL_TYPES = ['Both', 'Commercial Ship', 'Yacht'] as const;
export type ProvisionVesselType = (typeof VESSEL_TYPES)[number];

/**
 * AB'nin bildirilmesi zorunlu 14 alerjeni (Regulation (EU) No 1169/2011 Annex II), kütüphanenin
 * yazılışıyla. Kütüphanedeki etiket sezgisel başlangıçtır: ürün etiketiyle doğrulanır.
 */
export const ALLERGENS = [
  'Gluten',
  'Crustaceans',
  'Egg',
  'Fish',
  'Peanut',
  'Soy',
  'Milk',
  'Tree Nuts',
  'Celery',
  'Mustard',
  'Sesame',
  'Sulphites',
  'Lupin',
  'Molluscs',
] as const;
export type Allergen = (typeof ALLERGENS)[number];

/** Kalem kodu: şirkette tekil ve değişmez (ADR 0099 §2). */
export const ITEM_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{1,39}$/;

/** Kütüphanenin birimi → katalog birimi (ADR 0099 §3). */
export const LIBRARY_UNIT_MAP: Readonly<Record<string, MaterialUnit>> = {
  kg: 'KG',
  g: 'G',
  L: 'LTR',
  ml: 'ML',
  pack: 'PACK',
  can: 'CAN',
  jar: 'JAR',
  bottle: 'BTL',
  bunch: 'BUNCH',
  tub: 'TUB',
  pcs: 'PCS',
  box: 'BOX',
  roll: 'ROLL',
};

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const optionalOption = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .enum(values, typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? null);
const uniqueList = <T extends z.ZodType<string>>(item: T, max: number) =>
  z
    .array(item, typed('INVALID_LIST'))
    .max(max, 'TOO_LONG')
    .refine((list) => new Set(list).size === list.length, 'DUPLICATE_VALUE')
    .nullish()
    .transform((value) => value ?? null);

/** Kalemin kumanya alanları; hepsi isteğe bağlı (eski ve şirketin kendi kalemleri). */
export const provisionFieldsSchema = z.object({
  itemCode: z
    .string(typed('INVALID_TEXT'))
    .regex(ITEM_CODE_PATTERN, 'INVALID_ITEM_CODE')
    .nullish()
    .transform((value) => value ?? null),
  packSize: optionalText(60),
  storageType: optionalOption(STORAGE_TYPES),
  temperatureGuidance: optionalText(200),
  allergenTags: uniqueList(z.enum(ALLERGENS, typed('INVALID_OPTION')), ALLERGENS.length),
  /** Alerjen etiketleri ürün etiketiyle doğrulandı mı (kütüphane etiketi başlangıçtır). */
  allergenVerified: z
    .boolean(typed('INVALID_BOOLEAN'))
    .nullish()
    .transform((value) => value ?? false),
  dietaryTags: uniqueList(
    z.string(typed('INVALID_TEXT')).transform(collapse).pipe(z.string().min(1).max(100)),
    20,
  ),
  shelfLifeDays: z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(1, 'OUT_OF_RANGE')
    .max(3650, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null),
  batchTracking: optionalOption(TRACKING_MODES),
  expiryTracking: optionalOption(TRACKING_MODES),
  fefoRequired: z
    .boolean(typed('INVALID_BOOLEAN'))
    .nullish()
    .transform((value) => value ?? null),
  costCenter: optionalOption(COST_CENTERS),
  vesselType: optionalOption(VESSEL_TYPES),
  active: z
    .boolean(typed('INVALID_BOOLEAN'))
    .nullish()
    .transform((value) => value ?? true),
  librarySource: optionalText(60),
});
export type ProvisionFields = z.output<typeof provisionFieldsSchema>;

export function parseProvisionFields(input: unknown): ParseResult<ProvisionFields> {
  const result = provisionFieldsSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        // Liste öğesinin sorunu alanın sorunudur (`allergenTags.2` → `allergenTags`), alan başına bir
        issues: toIssues(result.error)
          .map((issue) => ({ ...issue, path: issue.path.split('.')[0] ?? issue.path }))
          .filter((issue, index, all) => all.findIndex((i) => i.path === issue.path) === index),
      };
}

/** Lot izlemeli kalem: batch ya da son kullanma izlemesi zorunlu (ADR 0099 §5). */
export function isLotTracked(fields: {
  batchTracking: string | null | undefined;
  expiryTracking: string | null | undefined;
}): boolean {
  return fields.batchTracking === 'Yes' || fields.expiryTracking === 'Yes';
}
